import React from "react";
import linkExpired from "Assets/interview/interview-theme/images/link-expired.webp";
import pageNotFound from "Assets/interview/interview-theme/images/page-not-found.webp";
import serverError from "Assets/interview/interview-theme/images/server-error.webp";
import underMaintenance from "Assets/interview/interview-theme/images/under-maintenance.webp";
import alreadyApplied from "Assets/interview/interview-theme/images/already-applied.webp";
import sessionMoved from "Assets/interview/interview-theme/images/session-moved.svg";
import preScreeningDisqualified from "Assets/interview/interview-theme/images/pre-screening-disqualified.webp";
import interviewDoesNotExist from "Assets/interview/interview-theme/images/interview-does-not-exist.svg";
import Bg from "Assets/interview/interview-theme/images/bg-abstract.svg";
import { getErrorData, getPrescreeningText, getShowRetryScreen } from "Features/interview/interviewSlice";
import { MdOutlineWifiOff } from 'react-icons/md';
import { useSelector } from "react-redux";

export const DeadStateScreen = ({ status }) => {
    const errorData = useSelector(getErrorData);
    const prescreeningText = useSelector(getPrescreeningText);
    const dateToFormat = errorData?.applied_date || errorData?.expired_date;
    const formattedDate = new Date(dateToFormat?.replace(" ", "T"))?.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
    const showRetryScreen = useSelector(getShowRetryScreen);

    // Each dead-state illustration carries its own description, so the accessibility
    // widget's image-description tooltip and any screen reader say something useful.
    const getImageAlt = () => {
        switch (status) {
            case "EXPIRED":
                return "A chain link breaking apart beside a red alert triangle";
            case "INVALID_LINK":
                return "Two chain links breaking apart beside a red alert triangle";
            case "page_not_found":
                return "A large 404 with a sad-faced sheet of paper";
            case "server_error":
                return "A large 500 with a sad-faced sheet of paper falling into a hole";
            case "under_maintenance":
                return "A monitor displaying an Under Maintenance sign, with a traffic cone and a gear";
            case "ALREADY_APPLIED":
                return "An application profile card marked with a large green tick";
            case "PRE_SCREENING_DISQUALIFY":
                return "A profile card with a checklist and a magnifying glass showing a red cross";
            case "DELETED":
                return "A profile card marked with a red cross, for an interview that no longer exists";
            case "MULTIPLE_SESSION_EXIT":
                return "Stacked browser windows with a red cross on the front one";
            default:
                return "";
        }
    };

    const getImage = () => {
        switch (status) {
            case "EXPIRED":
                return linkExpired;
            case "INVALID_LINK":
                return linkExpired;
            case "page_not_found":
                return pageNotFound;
            case "server_error":
                return serverError;
            case "under_maintenance":
                return underMaintenance;
            case "ALREADY_APPLIED":
                return alreadyApplied;
            case "PRE_SCREENING_DISQUALIFY":
                return preScreeningDisqualified;
            case "DELETED":
                return interviewDoesNotExist;
            case "MULTIPLE_SESSION_EXIT":
                return sessionMoved;
            case "INTEGRATION_PARTNER_ERROR":
                return serverError
            default:
                return null;
        }
    };
    const getHeading = () => {
        switch (status) {
            case "EXPIRED":
                return "The link has expired";
            case "INVALID_LINK":
                return "The link is not valid";
            case "page_not_found":
                return "Page Not Found";
            case "server_error":
                return "Internal Server Error";
            case "under_maintenance":
                return "We're Making Some Updates";
            case "ALREADY_APPLIED":
                return "Already Applied";
            case "PRE_SCREENING_DISQUALIFY":
                return "Thank you for your time.";
            case "DELETED":
                return "Interview does not exist";
            case "MULTIPLE_SESSION_EXIT":
                return "Session moved to another tab";
            case "INTEGRATION_PARTNER_ERROR":
                return "Integration Partner Error"
            default:
                return null;
        }
    };
    const getErrorMessage = () => {
        switch (status) {
            case "EXPIRED":
                return `The link to this interview has expired on ${formattedDate}. Please reach out to your recruiter for assistance.`;
            case "page_not_found":
                return "The page you are looking for does not exist. Please check the URL or contact support.";
            case "server_error":
                return "An unexpected error occurred on our server. Please try again later or contact support.";
            case "under_maintenance":
                return "Our system is currently undergoing maintenance. We apologize for the inconvenience and appreciate your patience.";
            case "ALREADY_APPLIED":
                return `You have already applied on ${formattedDate}.`;
            case "PRE_SCREENING_DISQUALIFY":
                return prescreeningText || "Unfortunately, you did not pass the pre-screening process.";
            case "DELETED":
                return "The interview you're looking for may have been deleted, removed, or the link may be incorrect.";
            case "MULTIPLE_SESSION_EXIT":
                return "This interview is now active on another tab or device.";
            case "INVALID_LINK":
                return "Please check your Interview Link";
            case "INTEGRATION_PARTNER_ERROR":
                return "Integration Partner Error"
            default:
                return null;
        }
    };

    const getMessage = () => {
        switch (status) {
            case "EXPIRED":
                return "Please check the link and try again.";
            case "page_not_found":
                return "It may have been moved or the URL may be incorrect.";
            case "server_error":
                return "Please try again later.";
            case "under_maintenance":
                return "Thanks for your patience. We'll be back online soon.";
            case "ALREADY_APPLIED":
                return "Please contact your recruiter.";
            case "PRE_SCREENING_DISQUALIFY":
                return "";
            case "DELETED":
                return null;
            case "MULTIPLE_SESSION_EXIT":
                return "This session has been closed.";
            default:
                return null;
        }
        return null;
    };
    
    const reload = () => {
        window.location.reload();
    }
    return (
        <>
        
            {showRetryScreen ? (
                <section className="py-5 d-flex align-items-center justify-content-center min-vh-100 w-100" style={{ background: `url(${Bg}) no-repeat center / cover` }}>
                    <div className="container">
                        <div className="row">
                        <div className="col-lg-8 col-xl-7 col-xxl-6 mx-auto text-center">
                            <MdOutlineWifiOff size={72} className="mb-4 text-muted" />
                            <h1 className="h2 mb-3">Unable to load the page</h1>
                            <p className="mb-2 text-muted">Please check your internet connection.</p>
                            <p className="text-muted">If you are on a work or school network, VPN, or using an ad-blocker, try disabling it or switching networks.</p>
                            <button type="button" className="btn btn-success px-4 mt-2" onClick={reload}>
                            Retry
                            </button>
                        </div>
                        </div>
                    </div>
                </section>  
            ):
            (
                <section className="py-5 d-flex overflow-auto justify-content-center h-100" style={{ background: `url(${Bg}) no-repeat center / cover` }}>
                    <div className="container">
                        <div className="row mb-5">
                            <div className="col-lg-9 col-xl-7 col-xxl-6 mx-auto text-center">
                                <figure className="mx-auto mb-4" style={{ maxWidth: 460 }}>
                                    <img src={getImage()} alt={getImageAlt()} className="img-fluid" />
                                </figure>
                                <h1 className="h2 fw-bold mb-3">{getHeading()}</h1>
                                {getErrorMessage() && <p className="mb-2 text-muted">{getErrorMessage()}</p>}
                                {getMessage() && <p className="mb-5 text-muted">{getMessage()}</p>}
                            </div>
                        </div>
                    </div>
                </section>
            )}       
        </>
    );
};
